import { useState, useRef, useEffect } from "react";
import { ChevronDownIcon, CheckIcon, PencilIcon, XIcon } from "lucide-react";
import { Input } from "@component/input";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@component/popover";
import { COUNTRY_CODES } from "./country-codes-data";
import { cn } from "@util/tailwind";

interface PhoneInputWithCountryProps {
    countryCode: string;
    value: string;
    onSave: (newCountryCode: string, newPhone: string) => void;
    className?: string;
    maxWidth?: string;
    placeholder?: string;
}

export default function PhoneInputWithCountry({
    countryCode,
    value,
    onSave,
    className,
    maxWidth = "180px",
    placeholder,
}: PhoneInputWithCountryProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const [countryCodeOpen, setCountryCodeOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [validationError, setValidationError] = useState<string | null>(null);
    const [selectedCountryCode, setSelectedCountryCode] = useState(countryCode);
    const [phoneNumber, setPhoneNumber] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const isInteractingWithDropdown = useRef(false);

    // Update when value prop changes
    useEffect(() => {
        setSelectedCountryCode(countryCode);
        setPhoneNumber(value);
        setEditValue(value);
    }, [value, countryCode]);

    const handleEdit = () => {
        setIsEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const validatePhoneNumber = (number: string, countryCode: string): { isValid: boolean; error?: string } => {
        // Remove all non-digit characters for validation
        const digitsOnly = number.replace(/\D/g, "");

        // Find the country info
        const country = COUNTRY_CODES.find(c => c.code === countryCode);

        if (!country) {
            return { isValid: false, error: "Invalid country code." };
        }

        // Check if empty
        if (digitsOnly.length === 0) {
            return { isValid: false, error: "Phone number is required." };
        }

        return { isValid: true };
    };

    const handleSave = () => {
        // Don't save if user is interacting with the dropdown
        if (isInteractingWithDropdown.current) {
            return;
        }

        // Validate phone number
        const validation = validatePhoneNumber(phoneNumber, selectedCountryCode);
        if (!validation.isValid) {
            setValidationError(validation.error || "Invalid phone number.");
            return;
        }

        onSave(selectedCountryCode, phoneNumber);
        setIsEditing(false);
        setSearchQuery("");
        setValidationError(null);
    };

    const handleCancel = () => {
        setSelectedCountryCode(countryCode);
        setPhoneNumber(value);
        setIsEditing(false);
        setSearchQuery("");
        setValidationError(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSave();
        } else if (e.key === "Escape") {
            handleCancel();
        }
    };

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        const sanitized = input.replace(/[^0-9\s\-()]/g, "");
        setPhoneNumber(sanitized);

        // Clear validation error when user types
        if (validationError) {
            setValidationError(null);
        }
    };

    const handleCountryCodeSelect = (code: string) => {
        setSelectedCountryCode(code);
        setCountryCodeOpen(false);
        isInteractingWithDropdown.current = false;

        // Reformat the phone number for the new country code
        const digitsOnly = phoneNumber.replace(/\D/g, "");
        const formatted = `${code}${digitsOnly}`;
        setPhoneNumber(formatted);

        // Clear validation error when country changes
        setValidationError(null);

        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const filteredCountries = COUNTRY_CODES.filter(
        (country) =>
            country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            country.code.includes(searchQuery) ||
            country.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Get the selected country info
    const selectedCountry = COUNTRY_CODES.find(
        (c) => c.code === selectedCountryCode
    );

    if (!isEditing) {
        // Has a value: clicking the text bubbles up to the row so the profile
        // drawer opens. Only the pencil button enters edit mode.
        if (phoneNumber) {
            return (
                <div
                    className={cn(
                        "group inline-flex items-center gap-1 rounded px-2 py-1",
                        className
                    )}
                    style={{ maxWidth }}
                >
                    <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                        {selectedCountryCode}{phoneNumber}
                    </span>
                    <button
                        type="button"
                        aria-label="Edit phone"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit();
                        }}
                        className="flex-shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    >
                        <PencilIcon className="w-3 h-3" />
                    </button>
                </div>
            );
        }

        // Empty: whole field acts as the "+ Add" affordance.
        return (
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                }}
                className={cn(
                    "group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-2 py-1 transition-colors",
                    className
                )}
                style={{ maxWidth }}
            >
                <span className="text-sm text-blue-600 dark:text-blue-400 block">
                    {placeholder || "Not Provided"}
                </span>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="relative inline-flex items-center h-8 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-950 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
            style={{ maxWidth: "100%" }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Country Code Selector */}
            <Popover
                open={countryCodeOpen}
                onOpenChange={(open) => {
                    setCountryCodeOpen(open);
                    isInteractingWithDropdown.current = open;
                }}
            >
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        role="combobox"
                        aria-expanded={countryCodeOpen}
                        className="flex items-center gap-1.5 px-2 h-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-l-md"
                        onMouseDown={(e) => {
                            e.preventDefault(); // Prevent blur on the input
                            isInteractingWithDropdown.current = true;
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setCountryCodeOpen(!countryCodeOpen);
                        }}
                    >
                        <span className="text-base">{selectedCountry?.flag || "🌐"}</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{selectedCountryCode}</span>
                        <ChevronDownIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[280px] p-0"
                    align="start"
                    onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur on the input
                    }}
                >
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                        <Input
                            placeholder="Search country..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-8 text-sm"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                        {filteredCountries.map((country) => (
                            <button
                                key={`${country.name}-${country.code}`}
                                onMouseDown={(e) => {
                                    e.preventDefault(); // Prevent blur on the input
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCountryCodeSelect(country.code);
                                }}
                                className={cn(
                                    "w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors",
                                    selectedCountryCode === country.code &&
                                    "bg-gray-100 dark:bg-gray-800"
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-base">{country.flag}</span>
                                    <span className="text-gray-900 dark:text-gray-100">
                                        {country.name}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500 dark:text-gray-400 text-xs">
                                        {country.code}
                                    </span>
                                    {selectedCountryCode === country.code && (
                                        <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                    )}
                                </div>
                            </button>
                        ))}
                        {filteredCountries.length === 0 && (
                            <div className="px-3 py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                                No countries found.
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            {/* Vertical Separator */}
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />

            {/* Phone Number Input */}
            <div className="flex items-center gap-2" style={maxWidth ? { maxWidth } : undefined}>
                <input
                    ref={inputRef}
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleSave}
                    placeholder="Enter phone number"
                    className={cn(
                        "flex-1 h-full px-2 text-sm bg-transparent border-0 outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500",
                        validationError && "text-red-600 dark:text-red-400"
                    )}
                    style={{ minWidth: "120px" }}
                />

                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        handleSave();
                    }}
                    className="flex-shrink-0 p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                >
                    <CheckIcon className="w-4 h-4" />
                </button>
                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        handleCancel();
                    }}
                    className="flex-shrink-0 p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                >
                    <XIcon className="w-4 h-4" />
                </button>
            </div>

            {/* Validation Error Tooltip */}
            {validationError && (
                <div className="absolute top-full left-0 mt-1 px-2 py-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-600 dark:text-red-400 whitespace-nowrap z-10">
                    {validationError}
                </div>
            )}
        </div>
    );
}